import { memo, ReactNode, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import {
    IArgumentMetadata,
    IPolicyCondition,
    IPolicyConditionWithArgMetadata,
    isCategoricalMetadata,
    SpecialValue,
} from '@tymely/atoms';

import { PredicateButton } from './PredicateButton';
import { SpecialValuesSelect } from './SpecialValues';

export type ConditionEditorProps<T extends IPolicyCondition | IPolicyConditionWithArgMetadata> = {
    title?: string;
    condition: T;
    setCondition: (condition?: IPolicyConditionWithArgMetadata) => void;
    argumentMetadata: IArgumentMetadata;
    disabled?: boolean;
    withSpecialValues?: boolean;
    disablePortal?: boolean;
    disableUnderline?: boolean;
    onInputFocusChange?: (focused: boolean) => void;
    dontCareTitle: string;
    variant?: 'outlined' | 'contained' | 'text';
};

export const BaseConditionEditor = memo(
    (
        props: {
            children?: ReactNode | ReactNode[];
        } & ConditionEditorProps<IPolicyCondition>,
    ) => {
        const handleSpecialChange = useCallback(
            (values: SpecialValue[]) => {
                props.setCondition({
                    argument_metadata: props.argumentMetadata,
                    argument_metadata_id: props.argumentMetadata.id,
                    ...props.condition,
                    value: { ...props.condition.value, special: values },
                } as IPolicyConditionWithArgMetadata);
            },
            [props.condition, props.argumentMetadata, props.setCondition],
        );

        const specialOptions = useMemo<SpecialValue[]>(() => {
            const options = [] as SpecialValue[];

            if (props.argumentMetadata.arg_type === 'SYSTEM_ARGUMENT') {
                options.push('none');
            } else {
                if (props.argumentMetadata.unspecifiable) {
                    options.push('unspecified');
                }

                if (props.argumentMetadata.unknowable) {
                    options.push('unknown');
                }

                if (props.argumentMetadata.options?.neitherable && isCategoricalMetadata(props.argumentMetadata)) {
                    options.push('neither');
                }
            }

            return options;
        }, [props.argumentMetadata]);

        const hasChildren = Boolean(props.children) && (props.children?.length ?? true);

        const hasSpecialValuesSelector = Boolean((props.withSpecialValues ?? true) && specialOptions.length);

        return (
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                height={1}
                flex={1}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    overflow="hidden"
                    flex={1}
                    gap={0.5}
                    width={1}
                    height={1}
                >
                    <PredicateButton {...props} fullWidth={!hasChildren} hasRightNeighbors={hasSpecialValuesSelector} />

                    {hasChildren && (
                        <Box lineHeight="initial" overflow="hidden" textOverflow="ellipsis" flex={1}>
                            {props.children}
                        </Box>
                    )}
                    {hasSpecialValuesSelector && (
                        <Box width="auto" marginLeft="auto">
                            <SpecialValuesSelect
                                values={props.condition.value?.special || []}
                                options={specialOptions}
                                disabled={props.disabled}
                                onChange={handleSpecialChange}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    },
);

BaseConditionEditor.displayName = 'BaseConditionEditor';
